import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { 
  Package, Plus, Search, Save, FileText, Truck, CheckCircle,
  RefreshCw, Trash2, Eye, Calendar, Building2, ArrowLeft,
  Calculator, History, X, Loader2, ShoppingCart, Warehouse,
  Store, AlertTriangle
} from 'lucide-react';
import { formatDate } from '@/utils/dateFormat';
import api, { productsAPI } from '@/services/api';

const GRN_SOURCES = {
  hq_transfer: { 
    label: 'HQ Transfer', 
    description: 'From Warehouse or Other Store',
    icon: Warehouse,
    color: 'blue'
  },
  central_vendor: { 
    label: 'Central Vendor', 
    description: 'Direct delivery from HQ PO',
    icon: Truck,
    color: 'purple',
    ownedOnly: true  // Only for owned stores
  },
  local_vendor_po: { 
    label: 'Local Vendor (PO)', 
    description: 'Against Local Purchase Order',
    icon: ShoppingCart,
    color: 'amber'
  },
  local_vendor_direct: { 
    label: 'Direct Entry', 
    description: 'Without Purchase Order',
    icon: FileText,
    color: 'gray'
  },
  franchise_invoice: {
    label: 'Franchise Invoice',
    description: 'Receive goods from owned store invoice',
    icon: Store,
    color: 'indigo',
    franchiseOnly: true  // Only for franchise stores
  }
};

const TAX_RATES = [
  { value: 0, label: '0%' },
  { value: 5, label: '5%' },
  { value: 12, label: '12%' },
  { value: 18, label: '18%' },
  { value: 28, label: '28%' }
];

const StoreGRNEntryMultiSource = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const storeId = user?.entity_id;
  const storeName = user?.entity_name;
  const isFranchise = user?.store_type === 'franchise';
  
  // Tab state
  const [activeTab, setActiveTab] = useState('create');
  
  // Source selection
  const [selectedSource, setSelectedSource] = useState(null);
  
  // Pending items for source selection
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [pendingCentralPOs, setPendingCentralPOs] = useState([]);
  const [pendingLocalPOs, setPendingLocalPOs] = useState([]);
  const [pendingFranchiseInvoices, setPendingFranchiseInvoices] = useState([]);
  const [localVendors, setLocalVendors] = useState([]);
  const [loadingPending, setLoadingPending] = useState(false);
  
  // Selected reference
  const [selectedReference, setSelectedReference] = useState(null);
  const [selectedVendor, setSelectedVendor] = useState(null);
  
  // GRN Form
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [notes, setNotes] = useState('');
  const [additionalCharges, setAdditionalCharges] = useState(0);
  const [billDiscount, setBillDiscount] = useState(0);
  const [roundOff, setRoundOff] = useState(0);
  const [grnItems, setGrnItems] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  // Product search for direct entry
  const [productSearch, setProductSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // GRN List
  const [grnList, setGrnList] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('');
  
  // View Dialog
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [viewGRN, setViewGRN] = useState(null);

  // Fetch pending items based on source
  const fetchPendingItems = useCallback(async (source) => {
    if (!source) return;
    
    setLoadingPending(true);
    try {
      if (source === 'hq_transfer') {
        const res = await api.get('/store/grn/pending/transfers');
        setPendingTransfers(res.data || []);
      } else if (source === 'central_vendor') {
        const res = await api.get('/store/grn/pending/central-vendor-pos');
        setPendingCentralPOs(res.data || []);
      } else if (source === 'local_vendor_po') {
        const res = await api.get('/store/grn/pending/local-vendor-pos');
        setPendingLocalPOs(res.data || []);
      } else if (source === 'local_vendor_direct') {
        const res = await api.get('/store/grn/local-vendors');
        setLocalVendors(res.data || []);
      } else if (source === 'franchise_invoice') {
        const res = await api.get('/store/grn/pending/franchise-invoices');
        setPendingFranchiseInvoices(res.data || []);
      }
    } catch (error) {
      console.error('Error fetching pending items:', error);
      toast({ title: 'Error', description: 'Failed to fetch data', variant: 'destructive' });
    } finally {
      setLoadingPending(false);
    }
  }, [toast]);

  // Fetch GRN list
  const fetchGRNList = useCallback(async () => {
    setListLoading(true);
    try {
      const params = {};
      if (sourceFilter) params.source_type = sourceFilter;
      
      const res = await api.get('/store/grn', { params });
      setGrnList(res.data?.items || []);
    } catch (error) {
      console.error('Error fetching GRN list:', error);
    } finally {
      setListLoading(false);
    }
  }, [sourceFilter]);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchGRNList();
    }
  }, [activeTab, fetchGRNList]);

  // Handle source selection
  const handleSourceSelect = (source) => {
    setSelectedSource(source);
    setSelectedReference(null);
    setSelectedVendor(null);
    setGrnItems([]);
    fetchPendingItems(source);
  };

  // Handle reference selection (Transfer/PO)
  const handleReferenceSelect = (ref) => {
    setSelectedReference(ref);
    
    // Pre-populate items from reference
    if (selectedSource === 'hq_transfer') {
      setGrnItems((ref.items || []).map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.sku || item.product_sku,
        ordered_qty: item.dispatched_qty || item.approved_qty || 0,
        received_qty: item.dispatched_qty || item.approved_qty || 0,
        accepted_qty: item.dispatched_qty || item.approved_qty || 0,
        rejected_qty: 0,
        rejection_reason: '',
        unit_price: item.transfer_price || item.unit_price || 0,
        discount_percent: 0,
        discount_amount: 0,
        scheme_discount_percent: 0,
        scheme_discount_amount: 0,
        tax_rate: item.tax_rate || 18,
        mrp: item.mrp || 0,
        batch_number: '',
        expiry_date: ''
      })));
    } else if (selectedSource === 'central_vendor' || selectedSource === 'local_vendor_po') {
      setGrnItems((ref.items || []).map(item => {
        // Use ordered_qty (backend field) or fallback to quantity
        const orderedQty = item.ordered_qty || item.quantity || 0;
        const receivedQty = item.received_qty || 0;
        const pendingQty = item.pending_qty || Math.max(0, orderedQty - receivedQty);
        
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.sku || item.product_sku,
          ordered_qty: orderedQty,
          received_qty: pendingQty,  // Default to pending qty
          accepted_qty: pendingQty,
          rejected_qty: 0,
          rejection_reason: '',
          unit_price: item.unit_price || 0,
          discount_percent: item.discount_percent || 0,
          discount_amount: item.discount_amount || 0,
          scheme_discount_percent: item.scheme_discount_percent || 0,
          scheme_discount_amount: item.scheme_discount_amount || 0,
          tax_rate: item.tax_rate || 18,
          mrp: item.mrp || item.selling_price || 0,
          batch_number: '',
          expiry_date: ''
        };
      }));
      
      // Set vendor from reference
      if (ref.vendor_details) {
        setSelectedVendor({
          id: ref.vendor_id,
          name: ref.vendor_name || ref.vendor_details?.name,
          code: ref.vendor_code || ref.vendor_details?.code
        });
      }
    } else if (selectedSource === 'franchise_invoice') {
      // For franchise invoice, load items from the invoice
      setGrnItems((ref.items || []).map(item => {
        const orderedQty = item.quantity || item.ordered_qty || 0;
        const receivedQty = item.received_qty || 0;
        const pendingQty = item.pending_qty || Math.max(0, orderedQty - receivedQty);
        
        return {
          product_id: item.product_id,
          product_name: item.product_name,
          product_sku: item.sku || item.product_sku,
          ordered_qty: orderedQty,
          received_qty: pendingQty,
          accepted_qty: pendingQty,
          rejected_qty: 0,
          rejection_reason: '',
          unit_price: item.unit_price || 0,
          discount_percent: item.discount_percent || 0,
          discount_amount: 0,
          scheme_discount_percent: 0,
          scheme_discount_amount: 0,
          tax_rate: item.tax_rate || 18,
          mrp: item.mrp || 0,
          batch_number: '',
          expiry_date: ''
        };
      }));
      
      // Set invoice number from reference
      setInvoiceNumber(ref.invoice_number || '');
    }
  };

  // Handle vendor selection for direct entry
  const handleVendorSelect = (vendor) => {
    setSelectedVendor(vendor);
    setGrnItems([]);
  };

  // Product search for direct entry
  const handleProductSearch = async (search) => {
    setProductSearch(search);
    if (search.length < 2) {
      setSearchResults([]);
      return;
    }
    
    setSearchLoading(true);
    try {
      const res = await productsAPI.list({ search, is_active: true, limit: 10 });
      setSearchResults(res.data?.items || res.data || []);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Add product to GRN
  const handleAddProduct = (product) => {
    const exists = grnItems.find(i => i.product_id === product.id);
    if (exists) {
      toast({ title: 'Info', description: 'Product already added' });
      return;
    }
    
    setGrnItems(prev => [...prev, {
      product_id: product.id,
      product_name: product.name,
      product_sku: product.sku,
      ordered_qty: 0,
      received_qty: 1,
      accepted_qty: 1,
      rejected_qty: 0,
      rejection_reason: '',
      unit_price: product.current_price?.cost_price || 0,
      discount_percent: 0,
      discount_amount: 0,
      scheme_discount_percent: 0,
      scheme_discount_amount: 0,
      tax_rate: product.tax_rate || 18,
      mrp: product.current_price?.mrp || 0,
      batch_number: '',
      expiry_date: ''
    }]);
    
    setProductSearch('');
    setSearchResults([]);
  };

  // Update item
  const handleUpdateItem = (index, field, value) => {
    setGrnItems(prev => prev.map((item, i) => {
      if (i !== index) return item;
      
      const isNumeric = ['received_qty', 'accepted_qty', 'rejected_qty', 'unit_price', 
        'discount_percent', 'discount_amount', 'scheme_discount_percent', 
        'scheme_discount_amount', 'tax_rate', 'mrp'].includes(field);
      
      const updated = { ...item, [field]: isNumeric ? (parseFloat(value) || 0) : value };
      
      // Auto-calculate accepted/rejected
      if (field === 'received_qty') {
        updated.accepted_qty = Math.max(0, updated.received_qty - updated.rejected_qty);
      }
      if (field === 'rejected_qty') {
        updated.accepted_qty = Math.max(0, updated.received_qty - updated.rejected_qty);
      }
      
      return updated;
    }));
  };

  // Remove item
  const handleRemoveItem = (index) => {
    setGrnItems(prev => prev.filter((_, i) => i !== index));
  };

  // Calculate item values including purchase profit
  const calculateItemValues = (item) => {
    const grossAmount = item.accepted_qty * item.unit_price;
    const afterDiscPercent = grossAmount * (1 - (item.discount_percent || 0) / 100);
    const afterDiscAmount = afterDiscPercent - (item.discount_amount || 0);
    const afterSchemePercent = afterDiscAmount * (1 - (item.scheme_discount_percent || 0) / 100);
    const netAmount = Math.max(0, afterSchemePercent - (item.scheme_discount_amount || 0));
    const taxAmount = netAmount * ((item.tax_rate || 0) / 100);
    const lineTotal = netAmount + taxAmount;
    
    // Calculate net unit cost (per item after all discounts, excluding tax)
    const netUnitCost = item.accepted_qty > 0 ? netAmount / item.accepted_qty : item.unit_price;
    
    // Calculate purchase profit % = ((MRP - Net Unit Cost) / MRP) * 100
    const mrp = item.mrp || 0;
    const purchaseProfit = mrp > 0 ? ((mrp - netUnitCost) / mrp) * 100 : 0;
    
    return { grossAmount, netAmount, taxAmount, lineTotal, netUnitCost, purchaseProfit };
  };

  // Calculate totals
  const calculateTotals = () => {
    let subtotal = 0;
    let taxTotal = 0;
    let totalQty = 0;
    
    grnItems.forEach(item => {
      const values = calculateItemValues(item);
      subtotal += values.netAmount;
      taxTotal += values.taxAmount;
      totalQty += item.accepted_qty;
    });
    
    const addCharges = parseFloat(additionalCharges) || 0;
    const discount = parseFloat(billDiscount) || 0;
    const rndOff = parseFloat(roundOff) || 0;
    const grandTotal = subtotal + taxTotal + addCharges - discount + rndOff;
    
    return {
      subtotal: subtotal.toFixed(2),
      taxTotal: taxTotal.toFixed(2),
      additionalCharges: addCharges.toFixed(2),
      billDiscount: discount.toFixed(2),
      roundOff: rndOff.toFixed(2),
      grandTotal: grandTotal.toFixed(2),
      totalQty,
      totalItems: grnItems.length
    };
  };

  const totals = calculateTotals();

  // Submit GRN
  const handleSubmit = async () => {
    if (!selectedSource) {
      toast({ title: 'Error', description: 'Please select a source', variant: 'destructive' });
      return;
    }
    
    if (grnItems.length === 0) {
      toast({ title: 'Error', description: 'Please add at least one item', variant: 'destructive' });
      return;
    }
    
    // Validate based on source
    if (selectedSource === 'hq_transfer' && !selectedReference) {
      toast({ title: 'Error', description: 'Please select a transfer order', variant: 'destructive' });
      return;
    }
    if (selectedSource === 'central_vendor' && !selectedReference) {
      toast({ title: 'Error', description: 'Please select an HQ Purchase Order', variant: 'destructive' });
      return;
    }
    if (selectedSource === 'local_vendor_po' && !selectedReference) {
      toast({ title: 'Error', description: 'Please select a Local Purchase Order', variant: 'destructive' });
      return;
    }
    if (selectedSource === 'local_vendor_direct' && !selectedVendor) {
      toast({ title: 'Error', description: 'Please select a vendor', variant: 'destructive' });
      return;
    }
    if (selectedSource === 'franchise_invoice' && !selectedReference) {
      toast({ title: 'Error', description: 'Please select a franchise invoice', variant: 'destructive' });
      return;
    }
    
    setSubmitting(true);
    try {
      const payload = {
        source_type: selectedSource,
        transfer_order_id: selectedSource === 'hq_transfer' ? selectedReference?.id : null,
        hq_po_id: selectedSource === 'central_vendor' ? selectedReference?.id : null,
        local_po_id: selectedSource === 'local_vendor_po' ? selectedReference?.id : null,
        franchise_invoice_id: selectedSource === 'franchise_invoice' ? selectedReference?.id : null,
        vendor_id: selectedVendor?.id || null,
        invoice_number: invoiceNumber,
        invoice_date: invoiceDate || null,
        vehicle_number: vehicleNumber,
        notes: notes,
        additional_charges: parseFloat(additionalCharges) || 0,
        bill_discount: parseFloat(billDiscount) || 0,
        round_off: parseFloat(roundOff) || 0,
        items: grnItems.map(item => ({
          product_id: item.product_id,
          ordered_qty: item.ordered_qty,
          received_qty: item.received_qty,
          accepted_qty: item.accepted_qty,
          rejected_qty: item.rejected_qty,
          rejection_reason: item.rejection_reason,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent,
          discount_amount: item.discount_amount,
          scheme_discount_percent: item.scheme_discount_percent,
          scheme_discount_amount: item.scheme_discount_amount,
          tax_rate: item.tax_rate,
          mrp: item.mrp,
          batch_number: item.batch_number,
          expiry_date: item.expiry_date
        }))
      };
      
      const res = await api.post('/store/grn', payload);
      
      toast({ 
        title: 'Success', 
        description: `GRN ${res.data.grn_number} created successfully`,
        variant: 'default'
      });
      
      // Reset form
      setSelectedSource(null);
      setSelectedReference(null);
      setSelectedVendor(null);
      setGrnItems([]);
      setInvoiceNumber('');
      setInvoiceDate(new Date().toISOString().split('T')[0]);
      setVehicleNumber('');
      setNotes('');
      setAdditionalCharges(0);
      setBillDiscount(0);
      setRoundOff(0);
      
    } catch (error) {
      console.error('Submit error:', error);
      toast({ 
        title: 'Error', 
        description: error.response?.data?.detail || 'Failed to create GRN',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Get available sources based on store type
  const getAvailableSources = () => {
    const sources = Object.entries(GRN_SOURCES);
    if (isFranchise) {
      // Franchise stores don't get central_vendor (they go via HQ ledger from transfer)
      return sources.filter(([key, val]) => !val.ownedOnly);
    }
    return sources;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <Toaster />
      
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Package className="w-6 h-6 text-teal-600" /> GRN Entry
          </h1>
          <p className="text-gray-500">{storeName} | Goods Receipt Note</p>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-gray-100">
          <TabsTrigger value="create" className="data-[state=active]:bg-white">
            <Plus className="w-4 h-4 mr-2" /> Create GRN
          </TabsTrigger>
          <TabsTrigger value="history" className="data-[state=active]:bg-white">
            <History className="w-4 h-4 mr-2" /> History
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="create" className="mt-6">
          {/* Step 1: Source Selection */}
          {!selectedSource && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <CardTitle className="text-gray-800">Select GRN Source</CardTitle>
                <CardDescription>Choose where the goods are coming from</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                  {getAvailableSources().map(([key, source]) => {
                    const Icon = source.icon;
                    return (
                      <div
                        key={key}
                        onClick={() => handleSourceSelect(key)}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all hover:shadow-md
                          border-gray-200 hover:border-${source.color}-400 bg-white`}
                      >
                        <div className="flex flex-col items-center text-center gap-2">
                          <div className={`p-3 rounded-full bg-${source.color}-100`}>
                            <Icon className={`w-6 h-6 text-${source.color}-600`} />
                          </div>
                          <p className="font-medium text-gray-800">{source.label}</p>
                          <p className="text-xs text-gray-500">{source.description}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          
          {/* Step 2: Source-specific selection */}
          {selectedSource && !selectedReference && selectedSource !== 'local_vendor_direct' && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-800 flex items-center gap-2">
                      {selectedSource === 'hq_transfer' && <Warehouse className="w-5 h-5" />}
                      {selectedSource === 'central_vendor' && <Truck className="w-5 h-5" />}
                      {selectedSource === 'local_vendor_po' && <ShoppingCart className="w-5 h-5" />}
                      {selectedSource === 'franchise_invoice' && <Store className="w-5 h-5" />}
                      Select {GRN_SOURCES[selectedSource]?.label}
                    </CardTitle>
                    <CardDescription>Choose the reference document</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => setSelectedSource(null)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingPending ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                    <p className="text-gray-500 mt-2">Loading...</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {selectedSource === 'hq_transfer' && pendingTransfers.map(transfer => (
                      <div
                        key={transfer.id}
                        onClick={() => handleReferenceSelect(transfer)}
                        className="p-4 rounded-lg border border-gray-200 hover:border-blue-400 cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{transfer.transfer_number}</p>
                            <p className="text-sm text-gray-500">
                              From: {transfer.source_details?.name || transfer.source_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {transfer.items?.length || 0} items | {formatDate(transfer.created_at)}
                            </p>
                          </div>
                          <Badge className={`${
                            transfer.status === 'dispatched' ? 'bg-purple-100 text-purple-700' :
                            transfer.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                            'bg-teal-100 text-teal-700'
                          }`}>
                            {transfer.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    
                    {selectedSource === 'central_vendor' && pendingCentralPOs.map(po => (
                      <div
                        key={po.id}
                        onClick={() => handleReferenceSelect(po)}
                        className="p-4 rounded-lg border border-gray-200 hover:border-purple-400 cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{po.po_number}</p>
                            <p className="text-sm text-gray-500">
                              Vendor: {po.vendor_details?.name || po.vendor_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {po.items?.length || 0} items | {formatDate(po.created_at)}
                            </p>
                          </div>
                          <Badge className="bg-purple-100 text-purple-700">
                            {po.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    
                    {selectedSource === 'local_vendor_po' && pendingLocalPOs.map(po => (
                      <div
                        key={po.id}
                        onClick={() => handleReferenceSelect(po)}
                        className="p-4 rounded-lg border border-gray-200 hover:border-amber-400 cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{po.po_number || po.lpo_number}</p>
                            <p className="text-sm text-gray-500">
                              Vendor: {po.vendor_details?.name || po.vendor_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {po.items?.length || 0} items | {formatDate(po.created_at)}
                            </p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700">
                            {po.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    
                    {selectedSource === 'franchise_invoice' && pendingFranchiseInvoices.map(invoice => (
                      <div
                        key={invoice.id}
                        onClick={() => handleReferenceSelect(invoice)}
                        className="p-4 rounded-lg border border-gray-200 hover:border-indigo-400 cursor-pointer transition-all"
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium text-gray-800">{invoice.invoice_number}</p>
                            <p className="text-sm text-gray-500">
                              From: {invoice.seller_details?.name || invoice.seller_store_name}
                            </p>
                            <p className="text-xs text-gray-400">
                              {invoice.items?.length || 0} items | ₹{invoice.grand_total?.toFixed(2)} | {formatDate(invoice.created_at)}
                            </p>
                          </div>
                          <Badge className="bg-indigo-100 text-indigo-700">
                            {invoice.grn_status || 'pending'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                    
                    {/* Empty state */}
                    {(
                      (selectedSource === 'hq_transfer' && pendingTransfers.length === 0) ||
                      (selectedSource === 'central_vendor' && pendingCentralPOs.length === 0) ||
                      (selectedSource === 'local_vendor_po' && pendingLocalPOs.length === 0) ||
                      (selectedSource === 'franchise_invoice' && pendingFranchiseInvoices.length === 0)
                    ) && (
                      <div className="text-center py-8">
                        <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No pending items found</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Vendor selection for direct entry */}
          {selectedSource === 'local_vendor_direct' && !selectedVendor && (
            <Card className="bg-white border-gray-200 shadow-sm">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="text-gray-800">Select Local Vendor</CardTitle>
                    <CardDescription>Direct entry without purchase order</CardDescription>
                  </div>
                  <Button variant="outline" onClick={() => setSelectedSource(null)}>
                    <ArrowLeft className="w-4 h-4 mr-2" /> Back
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {loadingPending ? (
                  <div className="text-center py-8">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                  </div>
                ) : localVendors.length === 0 ? (
                  <div className="text-center py-8">
                    <AlertTriangle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No local vendors available</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {localVendors.map(vendor => (
                      <div
                        key={vendor.id}
                        onClick={() => handleVendorSelect(vendor)}
                        className="p-4 rounded-lg border border-gray-200 hover:border-amber-400 cursor-pointer transition-all"
                      >
                        <p className="font-medium text-gray-800">{vendor.name}</p>
                        <p className="text-sm text-gray-500">{vendor.code}</p>
                        {vendor.contact?.phone && (
                          <p className="text-xs text-gray-400">{vendor.contact.phone}</p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          
          {/* Step 3: GRN Entry Form */}
          {(selectedReference || (selectedSource === 'local_vendor_direct' && selectedVendor)) && (
            <div className="space-y-6">
              {/* Header info */}
              <Card className="bg-gradient-to-r from-teal-50 to-emerald-50 border-teal-200">
                <CardContent className="py-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Badge className={`bg-${GRN_SOURCES[selectedSource]?.color}-100 text-${GRN_SOURCES[selectedSource]?.color}-700`}>
                        {GRN_SOURCES[selectedSource]?.label}
                      </Badge>
                      {selectedReference && (
                        <span className="font-medium text-gray-800">
                          Ref: {selectedReference.transfer_number || selectedReference.po_number || selectedReference.lpo_number}
                        </span>
                      )}
                      {selectedVendor && (
                        <span className="font-medium text-gray-800">
                          Vendor: {selectedVendor.name}
                        </span>
                      )}
                    </div>
                    <Button variant="outline" onClick={() => {
                      setSelectedReference(null);
                      setSelectedVendor(null);
                      setGrnItems([]);
                    }}>
                      <X className="w-4 h-4 mr-2" /> Change
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              {/* Invoice Details */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-800 text-base">Invoice Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="space-y-2">
                      <Label className="text-gray-700">Invoice Number</Label>
                      <Input
                        value={invoiceNumber}
                        onChange={(e) => setInvoiceNumber(e.target.value)}
                        placeholder="INV-001"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Invoice Date</Label>
                      <Input
                        type="date"
                        value={invoiceDate}
                        onChange={(e) => setInvoiceDate(e.target.value)}
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Vehicle Number</Label>
                      <Input
                        value={vehicleNumber}
                        onChange={(e) => setVehicleNumber(e.target.value)}
                        placeholder="TN01AB1234"
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-gray-700">Notes</Label>
                      <Input
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        placeholder="Any notes..."
                        className="bg-gray-50 border-gray-300"
                      />
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              {/* Product Search (for direct entry) */}
              {selectedSource === 'local_vendor_direct' && (
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-gray-800 text-base">Add Products</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search products by name or SKU..."
                        value={productSearch}
                        onChange={(e) => handleProductSearch(e.target.value)}
                        className="pl-10 bg-gray-50 border-gray-300"
                      />
                      {searchResults.length > 0 && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {searchResults.map(product => (
                            <div
                              key={product.id}
                              onClick={() => handleAddProduct(product)}
                              className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-gray-100"
                            >
                              <p className="font-medium text-gray-800">{product.name}</p>
                              <p className="text-xs text-gray-500">{product.sku} | MRP: ₹{product.current_price?.mrp || 0}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Items Table */}
              <Card className="bg-white border-gray-200 shadow-sm">
                <CardHeader className="pb-3">
                  <CardTitle className="text-gray-800 text-base">Items ({grnItems.length})</CardTitle>
                </CardHeader>
                <CardContent>
                  {grnItems.length > 0 ? (
                    <div className="border border-gray-200 rounded-lg overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-teal-600 text-white">
                            <TableHead className="text-white font-semibold text-xs">#</TableHead>
                            <TableHead className="text-white font-semibold text-xs">Product</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Ord</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Rcvd</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Acpt</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Rate</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Dis%</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Dis₹</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">S.Dis%</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">S.Dis₹</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Tax%</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">MRP</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-center">Profit%</TableHead>
                            <TableHead className="text-white font-semibold text-xs text-right">Amount</TableHead>
                            <TableHead className="w-8"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {grnItems.map((item, index) => {
                            const values = calculateItemValues(item);
                            const profitColor = values.purchaseProfit >= 20 ? 'text-emerald-600' : 
                                               values.purchaseProfit >= 10 ? 'text-amber-600' : 'text-red-600';
                            return (
                              <TableRow key={index} className="border-gray-200">
                                <TableCell className="text-gray-500 text-xs">{index + 1}</TableCell>
                                <TableCell>
                                  <p className="font-medium text-gray-800 text-xs">{item.product_name}</p>
                                  <p className="text-xs text-gray-500">{item.product_sku}</p>
                                </TableCell>
                                <TableCell className="text-gray-600 text-center text-xs">{item.ordered_qty}</TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={item.received_qty}
                                    onChange={(e) => handleUpdateItem(index, 'received_qty', e.target.value)}
                                    className="bg-amber-50 border-amber-200 h-7 w-16 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={item.accepted_qty}
                                    onChange={(e) => handleUpdateItem(index, 'accepted_qty', e.target.value)}
                                    className="bg-emerald-50 border-emerald-200 h-7 w-16 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.unit_price}
                                    onChange={(e) => handleUpdateItem(index, 'unit_price', e.target.value)}
                                    className="bg-gray-50 border-gray-300 h-7 w-20 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={item.discount_percent}
                                    onChange={(e) => handleUpdateItem(index, 'discount_percent', e.target.value)}
                                    className="bg-blue-50 border-blue-200 h-7 w-14 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.discount_amount}
                                    onChange={(e) => handleUpdateItem(index, 'discount_amount', e.target.value)}
                                    className="bg-blue-50 border-blue-200 h-7 w-16 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="0.1"
                                    value={item.scheme_discount_percent}
                                    onChange={(e) => handleUpdateItem(index, 'scheme_discount_percent', e.target.value)}
                                    className="bg-purple-50 border-purple-200 h-7 w-14 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.scheme_discount_amount}
                                    onChange={(e) => handleUpdateItem(index, 'scheme_discount_amount', e.target.value)}
                                    className="bg-purple-50 border-purple-200 h-7 w-16 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    max="100"
                                    value={item.tax_rate}
                                    onChange={(e) => handleUpdateItem(index, 'tax_rate', e.target.value)}
                                    className="bg-gray-50 border-gray-300 h-7 w-14 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell>
                                  <Input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    value={item.mrp}
                                    onChange={(e) => handleUpdateItem(index, 'mrp', e.target.value)}
                                    className="bg-orange-50 border-orange-200 h-7 w-20 text-xs text-center"
                                  />
                                </TableCell>
                                <TableCell className={`text-center font-semibold text-xs ${profitColor}`}>
                                  {item.mrp > 0 ? `${values.purchaseProfit.toFixed(1)}%` : '-'}
                                </TableCell>
                                <TableCell className="text-right font-medium text-gray-800 text-xs">
                                  ₹{values.lineTotal.toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {selectedSource === 'local_vendor_direct' && (
                                    <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => handleRemoveItem(index)}>
                                      <Trash2 className="w-3 h-3 text-red-500" />
                                    </Button>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  ) : (
                    <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                      <Package className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                      <p className="text-gray-500">No items added yet</p>
                    </div>
                  )}
                </CardContent>
              </Card>
              
              {/* Footer - Adjustments & Totals */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Adjustments */}
                <Card className="bg-white border-gray-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-gray-800 text-base">Adjustments</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">Add. Charges</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={additionalCharges}
                          onChange={(e) => setAdditionalCharges(e.target.value)}
                          className="bg-amber-50 border-amber-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">Bill Discount</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={billDiscount}
                          onChange={(e) => setBillDiscount(e.target.value)}
                          className="bg-emerald-50 border-emerald-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-gray-700 text-sm">Round Off</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={roundOff}
                          onChange={(e) => setRoundOff(e.target.value)}
                          className="bg-gray-50 border-gray-300"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                {/* Summary */}
                <Card className="bg-gradient-to-br from-amber-50 to-orange-50 border-amber-200 shadow-sm">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-amber-800 text-base flex items-center gap-2">
                      <Calculator className="w-4 h-4" /> Summary
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Items / Qty:</span>
                        <span className="font-medium">{totals.totalItems} / {totals.totalQty}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span>₹{totals.subtotal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Tax:</span>
                        <span>₹{totals.taxTotal}</span>
                      </div>
                      {parseFloat(totals.additionalCharges) > 0 && (
                        <div className="flex justify-between text-amber-700">
                          <span>+ Charges:</span>
                          <span>₹{totals.additionalCharges}</span>
                        </div>
                      )}
                      {parseFloat(totals.billDiscount) > 0 && (
                        <div className="flex justify-between text-emerald-700">
                          <span>- Discount:</span>
                          <span>₹{totals.billDiscount}</span>
                        </div>
                      )}
                      <hr className="border-amber-300" />
                      <div className="flex justify-between text-lg font-bold">
                        <span className="text-amber-800">Total:</span>
                        <span className="text-teal-600">₹{totals.grandTotal}</span>
                      </div>
                    </div>
                    
                    <Button 
                      className="w-full mt-4 bg-teal-600 hover:bg-teal-700"
                      onClick={handleSubmit}
                      disabled={submitting || grnItems.length === 0}
                    >
                      {submitting ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Save className="w-4 h-4 mr-2" />
                      )}
                      Create GRN
                    </Button>
                    
                    {isFranchise && selectedSource !== 'local_vendor_direct' && selectedSource !== 'local_vendor_po' && (
                      <p className="text-xs text-amber-700 mt-2 text-center">
                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                        This will update your HQ Ledger
                      </p>
                    )}
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
        
        {/* History Tab */}
        <TabsContent value="history" className="mt-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-gray-800">GRN History</CardTitle>
                <div className="flex gap-2">
                  <Select value={sourceFilter || 'all'} onValueChange={(val) => setSourceFilter(val === 'all' ? '' : val)}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sources</SelectItem>
                      <SelectItem value="hq_transfer">HQ Transfer</SelectItem>
                      <SelectItem value="central_vendor">Central Vendor</SelectItem>
                      <SelectItem value="local_vendor_po">Local Vendor PO</SelectItem>
                      <SelectItem value="local_vendor_direct">Direct Entry</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" onClick={fetchGRNList}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {listLoading ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-teal-600" />
                </div>
              ) : grnList.length === 0 ? (
                <div className="text-center py-8">
                  <FileText className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-gray-500">No GRN records found</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>GRN #</TableHead>
                      <TableHead>Source</TableHead>
                      <TableHead>Vendor/From</TableHead>
                      <TableHead>Items</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead className="w-10"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {grnList.map(grn => (
                      <TableRow key={grn.id}>
                        <TableCell className="font-medium">{grn.grn_number}</TableCell>
                        <TableCell>
                          <Badge className={`bg-${GRN_SOURCES[grn.source_type]?.color || 'gray'}-100 text-${GRN_SOURCES[grn.source_type]?.color || 'gray'}-700`}>
                            {GRN_SOURCES[grn.source_type]?.label || grn.source_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {grn.vendor_name || grn.source_ref?.source_name || '-'}
                        </TableCell>
                        <TableCell>{grn.items?.length || 0}</TableCell>
                        <TableCell>{formatDate(grn.created_at)}</TableCell>
                        <TableCell className="text-right font-medium">₹{grn.grand_total?.toFixed(2)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => { setViewGRN(grn); setShowViewDialog(true); }}
                          >
                            <Eye className="w-4 h-4 text-gray-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* View Dialog */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" /> GRN Details - {viewGRN?.grn_number}
            </DialogTitle>
          </DialogHeader>
          {viewGRN && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Source</p>
                  <p className="font-medium">{GRN_SOURCES[viewGRN.source_type]?.label}</p>
                </div>
                <div>
                  <p className="text-gray-500">Vendor/From</p>
                  <p className="font-medium">{viewGRN.vendor_name || viewGRN.source_ref?.source_name || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Invoice #</p>
                  <p className="font-medium">{viewGRN.invoice_number || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Date</p>
                  <p className="font-medium">{formatDate(viewGRN.created_at)}</p>
                </div>
              </div>
              
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Product</TableHead>
                    <TableHead>Qty</TableHead>
                    <TableHead>Rate</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {viewGRN.items?.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <p className="font-medium">{item.product_name}</p>
                        <p className="text-xs text-gray-500">{item.product_sku}</p>
                      </TableCell>
                      <TableCell>{item.accepted_qty}</TableCell>
                      <TableCell>₹{item.unit_price}</TableCell>
                      <TableCell className="text-right">₹{item.line_total?.toFixed(2)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              <div className="border-t pt-4">
                <div className="flex justify-end">
                  <div className="w-48 space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span>Subtotal:</span>
                      <span>₹{viewGRN.subtotal?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Tax:</span>
                      <span>₹{viewGRN.tax_total?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between font-bold text-lg border-t pt-1">
                      <span>Total:</span>
                      <span>₹{viewGRN.grand_total?.toFixed(2)}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreGRNEntryMultiSource;
